import {
  responsiveFontSizes,
  Theme,
  unstable_createMuiStrictModeTheme as createMuiTheme,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

export const plainStyle =
  ':not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)';

export const spacing = 10; // px

const fullScreen = {
  width: '100%',
  height: '100%',
};

const themeCommonColors = ['primary', 'secondary'] as const;
export const themeColors = [...themeCommonColors, 'default'] as const;
export const themeTextColors = [
  ...themeCommonColors,
  'textPrimary',
  'textSecondary',
  'error',
] as const;
export type ThemeColor = typeof themeColors[number];
export type ThemeTextColor = typeof themeTextColors[number];

export type ThemeProps = {
  theme: Theme;
};

export const CUSTOM_BLUE = '#6495ed';
export const CUSTOM_GREEN = '#8bbc29';

const baseTheme = createMuiTheme({
  palette: {
    primary: {
      light: '#6ABC35',
      main: '#31AD31',
      dark: '#11a111',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4A6670',
    },
    warning: {
      main: '#EEB20D',
    },
    error: {
      main: '#F6645B',
    },
    background: {
      default: '#FBFBFB',
    },
    misc: {
      lightGrey: '#EAEAEA',
      darkGrey: '#4E4E4E',
      black: '#333333',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.80)',
    },
  },
  typography: {
    fontFamily: [
      'Poppins',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      'color-emoji',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(', '),
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: {
      fontSize: '1em',
      fontWeight: 500,
    },
    body2: { fontWeight: 500 },
  },
  spacing,
  pad: (factor = 1) => `${factor * spacing}px`,
});

const augmentedTheme = createMuiTheme({
  ...baseTheme,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        ':root': fullScreen,
        '#root': fullScreen,
        body: {
          ...fullScreen,
          backgroundColor: 'white',
          // prevent callout to copy image, etc when tap to hold
          WebkitTouchCallout: 'none',
          // prevent webkit from resizing text to fit
          WebkitTextSizeAdjust: 'none',
          // prevent copy paste, to allow, change 'none' to 'text'
          WebkitUserSelect: 'none',
        },
        ol: {
          paddingInlineStart: baseTheme.typography.pxToRem(18),
          margin: 0,
        },
        a: {
          color: baseTheme.palette.primary.main,
          textDecoration: 'none',
        },
        label: {
          cursor: 'inherit',
        },
        button: {
          fontFamily: baseTheme.typography.fontFamily,
          fontWeight: 500,
        },
        '.MuiOutlinedInput-notchedOutline': {
          top: '-4px',
        },
        '.MuiButton-outlinedPrimary, .MuiButton-outlinedPrimary:hover':
          {
            borderColor: alpha(baseTheme.palette.primary.main, 0.6),
          },
        '.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]':
          {
            paddingTop: 0,
            paddingBottom: 0,
          },
        '.MuiAutocomplete-inputRoot input': {
          flexBasis: '100%;',
        },
        '.MuiTouchRipple-root': {
          zIndex: 1,
        },
        '.carousel .slide': {
          display: 'flex',
          justifyContent: 'flex-end',
        },
        '.carousel .slide img': {
          width: 'auto',
        },
        'img.SRLImage': {
          WebkitTouchCallout: 'default',
          WebkitUserSelect: 'initial',
        },
      },
    },
  },
});

const theme = responsiveFontSizes(augmentedTheme);

export default theme;
